import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import EstimateForm from '../../components/cutlist/EstimateForm';
import LineItemRow from '../../components/cutlist/LineItemRow';
import AdderRow from '../../components/cutlist/AdderRow';
import EstimateTotals from '../../components/cutlist/EstimateTotals';
import PaymentTerms, { DEFAULT_TERMS } from '../../components/cutlist/PaymentTerms';
import { generateEstimatePDF } from '../../pdf/estimatePDF';
import { useConfirm } from '../../components/ConfirmDialog';
import { ArrowLeft, Trash2, Download, Loader2, DollarSign, FileStack, Plus, PlusCircle } from 'lucide-react';

const WALNUT = '#8B4513';
const RUST   = '#A13D2D';
const OCHRE  = '#A6752A';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function makeRow(overrides = {}) {
  return {
    id: Date.now() + Math.random(),
    tag: 'manufacture',
    tagManual: false,
    qty: '1',
    description: '',
    room: '',
    size: '',
    ui: '',
    price: '',
    isAdder: false,
    ...overrides,
  };
}

const defaultEstimate = {
  customerName: '', phone: '', email: '', address: '', city: '',
  stateZip: '', date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
  contactDate: '', jobName: '', comments: '',
};

// Wider Type column (130px), taller rows via minHeight on cells
const TABLE_COLS = [
  { label: '#',           width: '36px'  },
  { label: 'Type',        width: '130px' }, // wider — dropdown fully visible
  { label: 'Qty',         width: '56px'  },
  { label: 'Description', width: 'auto'  },
  { label: 'Room',        width: '115px' },
  { label: 'Size',        width: '105px' },
  { label: 'UI',          width: '80px'  },
  { label: 'Unit $',      width: '95px'  },
  { label: 'Total',       width: '95px'  },
  { label: '',            width: '64px'  },
];

export default function Estimate() {
  const navigate = useNavigate();
  const [estimate,    setEstimate]    = useState(defaultEstimate);
  const [lineItems,   setLineItems]   = useState([makeRow()]);
  const [terms,       setTerms]       = useState(DEFAULT_TERMS);
  const confirm = useConfirm();
  const [downloading, setDownloading] = useState(false);

  function addRow()   { setLineItems(prev => [...prev, makeRow()]); }
  function addAdder() {
    setLineItems(prev => [...prev, makeRow({ tag: 'adder', tagManual: true, isAdder: true })]);
  }

  function updateRow(id, field, value) {
    setLineItems(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }

  function removeRow(id) { setLineItems(prev => prev.filter(r => r.id !== id)); }

  function duplicateRow(id) {
    setLineItems(prev => {
      const idx = prev.findIndex(r => r.id === id);
      if (idx === -1) return prev;
      const copy = { ...prev[idx], id: Date.now() + Math.random() };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }

  async function clearAll() {
    if (!(await confirm({ title: 'Clear everything and start over?', message: 'All entered windows will be lost.', tone: 'danger', confirmText: 'Clear All' }))) return;
    setEstimate(defaultEstimate);
    setLineItems([makeRow()]);
    setTerms(DEFAULT_TERMS);
  }
  const { userProfile } = useAuth();

  function handleDownload() {
    if (!estimate.customerName) { alert('Please enter a customer name first.'); return; }
    setDownloading(true);
    setTimeout(() => {
      try { generateEstimatePDF(estimate, lineItems, terms, userProfile || {}); }
      catch (e) { console.error(e); alert('Error generating PDF: ' + e.message); }
      finally { setTimeout(() => setDownloading(false), 800); }
    }, 100);
  }

  const breadcrumb = 'Dashboard › Cutlist Dashboard › Estimate Builder';

  return (
    <AppLayout
      title="Estimate Builder"
      breadcrumb={breadcrumb}
      topBarActions={
        <>
          <Button size="small" onClick={() => navigate(-1)}><ArrowLeft size={14} strokeWidth={2} /> Back</Button>
          <Button size="small" onClick={clearAll} style={{ background: RUST }}><Trash2 size={14} strokeWidth={2} /> Clear</Button>
          <Button size="small" onClick={handleDownload} disabled={downloading}>
            {downloading ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Download size={14} strokeWidth={2} />}
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </>
      }
    >
      <div style={{ maxWidth: '1500px', width: '100%', margin: '0 auto', boxSizing: 'border-box', fontFamily: FONT_SANS }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', fontSize: '13px', color: STONE }}>
          <DollarSign size={14} strokeWidth={2} color={WALNUT} />
          {estimate.customerName ? `${estimate.customerName} — ${lineItems.length} item${lineItems.length !== 1 ? 's' : ''}` : 'New Estimate'}
        </div>

        <EstimateForm estimate={estimate} onChange={setEstimate} />

        {/* Line Items Card */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden', marginBottom: '24px' }}>

          {/* Card header */}
          <div style={{ padding: '14px 20px', background: WALNUT, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileStack size={17} strokeWidth={2} color="white" />
            <div>
              <div style={{ color: 'white', fontWeight: '700', fontSize: '14px' }}>Line Items</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>
                {lineItems.length} item{lineItems.length !== 1 ? 's' : ''} — windows, doors, trim, adders
              </div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button type="button" onClick={addRow}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
                <Plus size={13} strokeWidth={2} /> Add Row
              </button>
              <button type="button" onClick={addAdder}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 16px', borderRadius: '8px', border: `1px solid ${OCHRE}88`, background: `${OCHRE}33`, color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = `${OCHRE}55`}
                onMouseLeave={e => e.currentTarget.style.background = `${OCHRE}33`}>
                <PlusCircle size={13} strokeWidth={2} /> Add Adder
              </button>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '900px' }}>
              <colgroup>
                {TABLE_COLS.map((col, i) => <col key={i} style={{ width: col.width }} />)}
              </colgroup>
              <thead>
                <tr style={{ background: 'rgba(139,69,19,0.04)', borderBottom: '2px solid rgba(139,69,19,0.12)' }}>
                  {TABLE_COLS.map((col, i) => (
                    <th key={i} style={{
                      padding: '11px 8px',
                      textAlign: i === 8 ? 'right' : i === 0 ? 'center' : 'left',
                      fontSize: '10.5px',
                      fontWeight: '700',
                      color: WALNUT,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      borderRight: i < TABLE_COLS.length - 1 ? '1px solid rgba(139,69,19,0.07)' : 'none',
                      paddingRight: i === 8 ? '12px' : '8px',
                      whiteSpace: 'nowrap',
                    }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((row, index) =>
                  row.isAdder
                    ? <AdderRow
                        key={row.id}
                        row={row}
                        index={index}
                        onChange={updateRow}
                        onRemove={removeRow}
                      />
                    : <LineItemRow
                        key={row.id}
                        row={row}
                        index={index}
                        onChange={updateRow}
                        onRemove={removeRow}
                        onDuplicate={duplicateRow}
                      />
                )}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: STONE, fontSize: '14px' }}>
                      No items yet — click <strong style={{ color: WALNUT }}>+ Add Row</strong> to get started
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom add bar */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(139,69,19,0.08)', background: 'rgba(139,69,19,0.02)', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button type="button" onClick={addRow}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 18px', borderRadius: '8px', border: '1px dashed rgba(139,69,19,0.3)', background: 'transparent', color: WALNUT, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,69,19,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <Plus size={13} strokeWidth={2} /> Add Row
            </button>
            <button type="button" onClick={addAdder}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 18px', borderRadius: '8px', border: `1px dashed ${OCHRE}55`, background: 'transparent', color: OCHRE, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = `${OCHRE}0d`; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <PlusCircle size={13} strokeWidth={2} /> Add Adder
            </button>
            <span style={{ marginLeft: 'auto', fontSize: '11px', color: STONE }}>
              {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Payment Terms + Totals */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px', alignItems: 'start' }}>
          <PaymentTerms terms={terms} onChange={setTerms} />
          <EstimateTotals lineItems={lineItems} />
        </div>
      </div>
    </AppLayout>
  );
}
